import React, { useState, useEffect, useRef } from 'react';
import { apiFetch, getApiHost } from '../utils/api';

interface RefreshModalProps {
  isOpen: boolean;
  requestId: string | null;
  previousCredits?: number | null;
  currentCredits?: number | null;
  onClose: () => void;
  onSuccess: () => void;
  onError: (error: string) => void;
}

const RefreshModal: React.FC<RefreshModalProps> = ({
  isOpen,
  requestId,
  previousCredits,
  currentCredits,
  onClose,
  onSuccess,
  onError
}) => {
  const [status, setStatus] = useState<string>('new');
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollingRef = useRef(false);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  // Keep refs in sync without triggering re-polls
  useEffect(() => { onSuccessRef.current = onSuccess; }, [onSuccess]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  useEffect(() => {
    if (isOpen && requestId) {
      setStatus('new');
      setProgress(null);
      pollingRef.current = true;
      setError(null);

      let shouldContinuePolling = true;

      const pollStatus = async () => {
        if (!requestId || !shouldContinuePolling) return;

        try {
          const response = await apiFetch(`${getApiHost()}/api/request-status?requestId=${requestId}`);
          if (!response.ok) {
            throw new Error('Failed to check status');
          }

          const data = await response.json();
          setStatus(data.status);
          if (data.progress) setProgress(data.progress);

          if (data.status === 'completed') {
            shouldContinuePolling = false;
            pollingRef.current = false;
            onSuccessRef.current();
          } else if (data.status === 'failed') {
            shouldContinuePolling = false;
            pollingRef.current = false;
            const errorMessage = data.failureReason || 'Analysis failed';
            setError(errorMessage);
            onErrorRef.current(errorMessage);
          } else {
            // Continue polling for other statuses
            setTimeout(pollStatus, 2000); // Poll every 2 seconds
          }
        } catch (err) {
          console.error('Error polling status:', err);
          shouldContinuePolling = false;
          pollingRef.current = false;
          const errorMessage = 'Failed to check analysis status';
          setError(errorMessage);
          onErrorRef.current(errorMessage);
        }
      };

      // Start polling immediately
      pollStatus();
    } else {
      pollingRef.current = false;
    }

    return () => {
      pollingRef.current = false;
    };
  }, [isOpen, requestId]);

  const getStatusMessage = () => {
    switch (status) {
      case 'new':
        return 'Request submitted, waiting to start...';
      case 'processing':
        return 'Analyzing token holders...';
      case 'completed':
        return 'Analysis completed successfully!';
      case 'failed':
        return 'Analysis failed';
      default:
        return 'Processing request...';
    }
  };

  const getStatusIcon = () => {
    if (error) {
      return <i className="fas fa-exclamation-triangle text-red-400 text-2xl"></i>;
    }
    if (status === 'completed') {
      return <i className="fas fa-check-circle text-green-400 text-2xl"></i>;
    }
    return <i className="fas fa-spinner fa-spin text-blue-400 text-2xl"></i>;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-crypto-card rounded-lg p-6 max-w-md w-full mx-4">
        <div className="text-center">
          <div className="mb-4">
            {getStatusIcon()}
          </div>

          <h3 className="text-xl font-semibold text-white mb-2">
            Token Analysis Refresh
          </h3>

          {progress && status !== 'new' ? (
            <div className="text-left bg-crypto-dark/50 rounded-lg p-3 mb-4 space-y-1.5">
              {progress.split('\n').map((line, i) => (
                <div key={i} className="text-sm text-gray-300 flex items-center space-x-2">
                  <span className="w-5 text-center">{line.split(' ')[0]}</span>
                  <span>{line.slice(line.indexOf(' ') + 1)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-300 mb-4">
              {getStatusMessage()}
            </p>
          )}

          {previousCredits != null && currentCredits != null && (
            <div className="bg-crypto-dark/50 rounded-lg px-4 py-2 mb-4 inline-flex items-center space-x-2 text-sm">
              <span className="text-gray-400">Credits:</span>
              <span className="text-yellow-400 font-medium">{previousCredits}</span>
              <span className="text-gray-500">→</span>
              <span className="text-crypto-accent font-medium">{currentCredits}</span>
            </div>
          )}

          {requestId && (
            <p className="text-xs text-gray-500 mb-4">
              Request ID: {requestId.slice(0, 8)}...
            </p>
          )}

          {error && (
            <div className="bg-red-900/20 border border-red-500 rounded-lg p-3 mb-4">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <div className="flex justify-center space-x-3">
            {status === 'completed' && (
              <button
                onClick={onClose}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            )}

            {error && (
              <button
                onClick={onClose}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            )}

            {status !== 'completed' && !error && (
              <button
                onClick={() => {
                  pollingRef.current = false;
                  onClose();
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefreshModal;
